import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryReports, LosReport } from '../../Data/ReportsData';
import { 
  Tabs,
  Tab,
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  ListItemSecondaryAction,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';

const getClientReportIdFromQueryParameter = (search: string): number | undefined => {
  const urlSearchParams = new URLSearchParams(search);
  let clientReportId: number | undefined = undefined;
  urlSearchParams.forEach((value, key) => {
    if (key?.toUpperCase() === "CLIENTREPORTID") {
      clientReportId = Number.parseInt(value);
    }
  });
  return clientReportId;
}

const DashboardComponent: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const clientReportId = getClientReportIdFromQueryParameter(location.search);
  const reports = useQueryReports();
  const selectedReport: LosReport | undefined = (clientReportId && reports.data)
    ? reports.data.flat.find(x => x.clientReportId === clientReportId)
    : undefined;
  const navigate = useNavigate();
  
  // Load script to display tableau report if necessary.
  useEffect(() => {
    if (selectedReport) {
      const script = document.createElement("script");
      script.src = "https://analyticspro.waterwheeldata.com/javascripts/api/viz_v1.js";
      script.async = true;
      document.body.append(script);
      return () => {
        script.remove();
      };
    }
  }, [selectedReport]);

  if (reports.isFetching) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  } else if (reports.error) {
    return <div style={{ padding: 24 }}>Error loading report.</div>;
  } else if (selectedReport) { // Display the report.
    return (
      <div key={selectedReport?.clientReportId} className="tableauPlaceholder" style={{ width: "100%", height: "85vh", marginTop: 24 }}>
        <object className="tableauViz" style={{ width: "100%", height: "85vh", display: "none" }}>
          <param name="host_url" value="https%3A%2F%2Fanalyticspro.waterwheeldata.com%2F" />
          <param name="site_root" value={`/t/${selectedReport.clientId}`} />
          <param name="name" value={`${selectedReport.reportId}/${selectedReport.defaultTabId}`} />
          <param name="tabs" value="yes" />
          <param name="toolbar" value="yes" />
          <param name="showShareOptions" value="true" />
        </object>
      </div>
    );
  } else { // Display the list of reports
    return (
      <div style={{
        flexGrow: 1,
        display: 'flex',
        padding: 24,
        width: '100%',
      }}>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            marginRight: theme.spacing(3),
          }}
          value={selectedCategory}
          onChange={(event, newValue: number) => setSelectedCategory(newValue)}
        >
          {reports.data?.grouped.map(x => 
            <Tab key={x.categoryName} label={x.categoryName} />
          )}
        </Tabs>
        {reports.data?.grouped
          ?.filter((x, i) => i === selectedCategory)
          ?.[0]
          ?.modules?.map(x => 
            <Paper key={x.moduleName} style={{ flex: "auto" }}>
              <List subheader={<ListSubheader>{x.moduleName}</ListSubheader>}>
                {x.reports.map(r => 
                  <ListItem key={r.reportName} component="button"
                    onClick={() => {
                      navigate({
                        search: `?clientReportId=${r.clientReportId}`,
                      })
                    }}
                  >
                    <ListItemText primary={r.reportName} />
                    <ListItemSecondaryAction>
                      <Tooltip title={r.reportDescription}>
                        <InfoIcon fontSize="inherit" color="action" style={{ marginLeft: '12px' }} />
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              </List>
            </Paper>
        )}
      </div>
    );
  }
}

export default DashboardComponent;